/**
 * Limits applied when generating the inline prompt preview shown
 * on each FTUE section card. We cap by both number of lines and total
 * characters so a single very long line cannot dominate the layout.
 */
const MAX_LINES = 5;
const MAX_CHARS = 800;

/**
 * Threshold (relative to MAX_CHARS) above which we prefer truncating
 * at the last word boundary instead of slicing mid-word.
 */
const WORD_BOUNDARY_RATIO = 0.6;

const ELLIPSIS = "…";

/**
 * Produce a short, human-readable preview of a section prompt.
 *
 * Rules:
 * - Keep at most {@link MAX_LINES} non-collapsed lines (preserve manual line breaks).
 * - Trim leading/trailing whitespace introduced by the slice.
 * - If the result still exceeds {@link MAX_CHARS}, cut to the character limit;
 *   prefer the last whitespace boundary when it is far enough into the text
 *   so the preview ends on a whole word.
 * - Append an ellipsis only when the original content was truncated.
 */
export function truncatePrompt(prompt: string | null | undefined): string {
  if (!prompt) return "";

  const allLines = prompt.split("\n");
  const limitedLines = allLines.slice(0, MAX_LINES).join("\n");
  const wasLineTruncated = allLines.length > MAX_LINES;

  const trimmed = limitedLines.trim();
  if (!trimmed) return "";

  if (trimmed.length <= MAX_CHARS) {
    return wasLineTruncated ? `${trimmed}${ELLIPSIS}` : trimmed;
  }

  const cut = trimmed.slice(0, MAX_CHARS);
  const lastSpace = cut.lastIndexOf(" ");
  const safeBoundary = MAX_CHARS * WORD_BOUNDARY_RATIO;
  const sliced = lastSpace > safeBoundary ? cut.slice(0, lastSpace) : cut;

  return `${sliced.trimEnd()}${ELLIPSIS}`;
}
